import { useCallback, useMemo } from 'react';
import { updateTaskStockClaims } from '@apis/schedule';
import type { ITaskStockClaim, SiteId, TaskId } from '@interfaces/IScheduler';
import { useImmediateWritePersist } from './useImmediateWritePersist';

/**
 * Immediate-write persistence for a task's stock claims ("Stock from CAD").
 * Claims are an EXECUTION fact (ruling 9): they live in the per-task activities
 * document and ride the same channel as status / areas / checklist ticks — a
 * per-task PUT, no schedule version, no clobbering by another editor's plan
 * Save. Returns `{ set(stockClaims) }`, which queues the WHOLE set on one
 * channel per task; a burst (Save right after a delivery Create) coalesces to
 * the last write. Retries are the recovery path; a set given up is rolled back
 * to the server's and a toast says so. No-op when `scheduleNumber` is absent (Router-free
 * tests) — the store still updates. Pending writes flush on unmount / task switch.
 */
export function useTaskStockClaimsPersist(
  siteId: SiteId,
  scheduleNumber: string | undefined,
  taskId: TaskId,
): { set: (stockClaims: ITaskStockClaim[]) => void } {
  const { write } = useImmediateWritePersist(siteId, scheduleNumber, taskId);

  const set = useCallback(
    (stockClaims: ITaskStockClaim[]) => {
      write<ITaskStockClaim[]>(['stockClaims'], stockClaims, (claims, ctx) =>
        updateTaskStockClaims(ctx.siteId, ctx.scheduleNumber, ctx.taskId, claims),
      );
    },
    [write],
  );

  return useMemo(() => ({ set }), [set]);
}
